import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import loadingLogo from '../assets/jerseys/Logo 36.png';

const API_URL = `${API_BASE_URL}/api/fantasy-teams/mine`;

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresTeam?: boolean;
  requiresConfirmedTeam?: boolean;
}

type LoadingState = 'loading' | 'auth' | 'create-team' | 'team-setup' | 'ready';

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiresTeam = false,
  requiresConfirmedTeam = false 
}) => {
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const location = useLocation();

  useEffect(() => {
    const initialize = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoadingState('auth');
        return;
      }

      // Si no requiere equipo, solo verificar token
      if (!requiresTeam && !requiresConfirmedTeam) {
        setLoadingState('ready');
        return;
      }

      try {
        const res = await fetch(API_URL, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (res.ok) {
          const team = await res.json();
          if (team && team.id) {
            localStorage.setItem('fantasyTeamId', team.id);
            
            if (requiresConfirmedTeam) {
              if (team.confirmed) {
                setLoadingState('ready');
              } else {
                setLoadingState('team-setup');
              }
            } else {
              setLoadingState('ready');
            }
          } else {
            setLoadingState('create-team');
          }
        } else if (res.status === 404) {
          if (requiresTeam || requiresConfirmedTeam) {
            setLoadingState('create-team');
          } else {
            setLoadingState('ready');
          }
        } else if (res.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('fantasyTeamId');
          setLoadingState('auth');
        } else {
          if (res.status === 404 && (requiresTeam || requiresConfirmedTeam)) {
            setLoadingState('create-team');
          } else {
            setLoadingState('auth');
          }
        }
      } catch (err) {
        if (err instanceof TypeError && err.message.includes('fetch')) {
          console.error('Error de red al inicializar:', err);
        }
        setLoadingState('auth');
      }
    };

    initialize();
  }, [requiresTeam, requiresConfirmedTeam]);

  if (loadingState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(to bottom right, #f0f9ff, #ffffff)' }}>
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl mb-4 shadow-2xl overflow-hidden">
            <img src={loadingLogo} alt="FantasyLub Logo" className="w-full h-full object-contain" />
          </div>
          <div className="text-blue-600 font-semibold">Cargando...</div>
        </div>
      </div>
    );
  }

  if (loadingState === 'auth') {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (loadingState === 'create-team') {
    return <Navigate to="/create-team" replace />;
  }

  if (loadingState === 'team-setup') {
    return <Navigate to="/ConfigurarEquipo" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
